(ns admin.components.forms.boost
  (:require [re-frame.core :as re-frame]
            [reagent.core :as reagent]
            [reagent-form.core :as rf]
            [reagent-form.transformers :as t]

            [kyber.components.base.text :as text]
            [kyber.components.base.button :as button]
            [kyber.components.base.link :as link]
            [kyber.styles.core :refer [gs styles->classes]]
            [kyber.components.composite.checkbox :as checkbox]

            [admin.events.floating :as >floating]

            [admin.utils :as au]
            [admin.components.composite.is-live-pill :as is-live-pill]
            [admin.components.forms.fields.markup-editor :as markup-editor]
            [admin.components.forms.fields.related-resources
             :as related-resources-field]
            [admin.components.forms.fields.toggle :as toggle]
            [kyber.components.form-fields.select :as select]
            [kyber.lib.kyber-db :as kyber-db]
            [admin.components.forms.fields.tag-collections :as tag-collections]
            [clojure.string :as str]
            [admin.events.boosts :as >boosts]))

(def classes
  (styles->classes
   {:form-contianer
    {:max-width "1200px"
     :margin :auto
     :border-radius (gs [:radius :r3])
     :box-shadow (gs [:shadows :shadow-6])
     :overflow :hidden
     :border (str "1px solid " (gs [:colors :gray-scale-200]))}

    :email-settings-section
    {:background-color (gs [:colors :gray-scale-50])
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))
     :display :flex
     :justify-content :space-between
     :padding [(gs [:spacing :p20])
               (gs [:spacing :p40])]}

    :tag-settings-section
    {:background-color (gs [:colors :gray-scale-50])
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))
     :padding [(gs [:spacing :p20])
               (gs [:spacing :p40])]}

    :section-container
    {:padding [(gs [:spacing :p20])
               (gs [:spacing :p0])]
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))
     :margin-bottom (gs [:spacing :p20])
     :width "100%"}

    :example-section
    {:display :flex
     :justify-content :space-between
     :align-items :flex-start

     "& > *"
     {"&:first-child"
      {:width "60%"}

      "&:last-child"
      {:width "calc(40% - 30px)"}}}

    :cta-container
    {:margin [(gs [:spacing :p40])
              (gs [:spacing :p20])
              (gs [:spacing :p20])
              (gs [:spacing :p40])]
     :display :flex
     :justify-content :space-between
     :align-items :flex-end
     :border-top (str "1px solid " (gs [:colors :gray-scale-200]))}

    :content-section
    {:padding [(gs [:spacing :p20])
               (gs [:spacing :p40])]}

    :content-section-title
    {:border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))
     :margin-bottom (gs [:spacing :p20])
     :padding-bottom (gs [:spacing :p20])}

    :example-image
    {:max-width "100%"}

    :boost-stat-container
    {:display :flex
     :align-items :flex-end

     "& > *:first-child"
     {:margin-right (gs [:spacing :p8])}}

    :questionnaire-help-container
    {:padding (gs [:spacing :p40])
     :background-color (gs [:colors :gray-scale-50])
     :border (str "1px solid " (gs [:colors :gray-scale-100]))}

    :cta-options-container
    {:display :flex
     :justify-content :space-between
     :align-items :flex-end
     :flex-wrap :wrap

     "& > *:first-child"
     {:width "30%"}

     "& > *:nth-child(2)"
     {:width "calc(70% - 20px)"}

     "& > *:nth-child(3)"
     {:margin-top (gs [:spacing :p40])}}

    :include-button-container
    {:display :flex
     :align-items :center
     :padding (gs [:spacing :p8])
     :background-color (gs [:colors :gray-scale-50])
     :border (str "1px solid " (gs [:colors :gray-scale-100]))
     :height "40px"
     :margin-bottom (gs [:spacing :p8])

     "& > *:first-child"
     {:margin-right (gs [:spacing :p12])}}

    :boost-time-container
    {:display :flex
     :align-items :center

     "& > p"
     {:margin [(gs [:spacing :p12])
               (gs [:spacing :p8])
               (gs [:spacing :p0])
               (gs [:spacing :p8])]}}

    :boost-name-container
    {:padding (gs [:spacing :p40])
     :background-color (gs [:colors :gray-scale-100])
     :display :flex
     :justify-content :space-between
     :align-items :center}

    :add-questionnaire-cta
    {:display :flex
     :justify-content :center
     :align-items :center
     :position :relative
     :z-index 1
     :background-color (gs [:colors :true-white])}

    :add-button-container
    {:padding [(gs [:spacing :p8])
               (gs [:spacing :p20])]
     :border (str "1px solid " (gs [:colors :gray-scale-200]))
     :border-radius [(gs [:radius :r0])
                     (gs [:radius :r0])
                     (gs [:radius :r3])
                     (gs [:radius :r3])]
     :margin-bottom (gs [:spacing :p20])}}))

(def ^:private boost-types
  {:trigger-when-activity-completed "trigger-when-activity-completed"
   :trigger-when-activity-started "trigger-when-activity-started"})

(def ^:private system-type
  {:immediate "immediate"
   :scheduled "scheduled"})

(defn- boost-condition
  ([boost]
   (boost-condition boost :present))
  ([{:keys [boost-type skip-when-activity-completed]} tense]
   (cond
     (and (= boost-type (:trigger-when-activity-started boost-types))
          (true? skip-when-activity-completed))
     (str "started the activity, but "
          (if (= tense :present) "has" "have")
          " not completed it.")

     (= boost-type (:trigger-when-activity-started boost-types))
     "started the activity."

     (= boost-type (:trigger-when-activity-completed boost-types))
     "completed the activity.")))

(defn- is-immediate? [boost]
  (-> boost :system-type (= (:immediate system-type))))

(def ^{:private true} seconds-in-a-minute 60)
(def ^{:private true} seconds-in-a-hour (* 60 60))
(def ^{:private true} seconds-in-a-day (* 60 60 24))
(defn- seconds->days [secs] (js/Math.floor
                             (/ secs seconds-in-a-day)))
(defn- seconds->hours [secs] (js/Math.floor
                              (/ (mod secs seconds-in-a-day)
                                 seconds-in-a-hour)))
(defn- seconds->minutes [secs] (js/Math.floor
                                (/ (mod secs seconds-in-a-hour)
                                   seconds-in-a-minute)))
(defn- days->seconds [days] (* (js/parseInt days) seconds-in-a-day))
(defn- hours->seconds [hours] (* (js/parseInt hours) seconds-in-a-hour))
(defn- minutes->seconds [minutes] (* (js/parseInt minutes) seconds-in-a-minute))

(defn- render-section-details
  [{:keys [label description]}]
  [:<>
   [text/title-20 :p label]
   [text/body-20 :p description]])

(defn- render-inline-name-editing
  [{:keys [boost-name] :as boost}]
  (let [is-editing (reagent/atom false)
        value (reagent/atom boost-name)]
    (fn [{:keys [event-key activity-uuid curriculum-uuid boost-uuid published publish-at]}]
      [:div {:class (:boost-name-container classes)}
       [:div
        [text/caption-20 :p "Name"]

        (if (false? @is-editing)
          [:div {:style {:display :flex
                         :align-items :center}}
           [text/title-40 :h3
            {:style {:margin-right (gs [:spacing :p12])}}
            @value]
           [button/button-link
            {:on-click #(reset! is-editing true)
             :type :button}
            "edit name"]]
          [:div {:style {:display :flex
                         :align-items :center}}
           [:input {:on-change #(reset! value (-> % .-target .-value))
                    :value @value
                    :type :text
                    :style {:max-width "600px"
                            :margin-right (gs [:spacing :p12])}}]
           [button/button-link
            {:on-click #(do
                          (reset! is-editing false)
                          (re-frame/dispatch
                           [event-key {:boost/name @value
                                       :boost/activity-uuid activity-uuid
                                       :curriculum-uuid curriculum-uuid
                                       :boost/uuid boost-uuid
                                       :boost/published published
                                       :boost/publish-at publish-at
                                       :prevent-redirect true}]))
             :type :button}
            "save"]])
        [text/caption-20 :p
         {:style {:color (gs [:colors :gray-scale-400])}}
         (str "This will only be displayed in the admin. Use a name that "
              "will help describe the boost.")]]
       [is-live-pill/render
        published (if (is-immediate? boost) "Sent" "Live")]])))

(defn filter-tag-groups
  [tag-groups tag-filter]
  (map (fn [group]
         (let [group-uuid (:content-provider.tag-group/uuid group)
               allowed-tags (->> (get tag-filter (keyword group-uuid) [])
                                 (map :tag-id)
                                 (set))
               filtered-values (->> (:content-provider.tag-group/values group)
                                    (filter #(contains? allowed-tags (:content-provider.tag/uuid %)))
                                    (vec))]
           (assoc group :content-provider.tag-group/values filtered-values)))
       tag-groups))

(defn has-tag-group-values?
  "Checks if any :content-provider.tag-group/values contains at least one value."
  [data]
  (some (fn [group] (seq (:content-provider.tag-group/values group))) data))


(defn- render-email-settings-section
  [{:keys [display-name subject category]} activity-valid-tags valid-tags]
  [:div
   (when (and (has-tag-group-values? activity-valid-tags)
              (not (= category "reengagement")))
     [:div {:class (:tag-settings-section classes)}
      [render-section-details
       {:label "Recipients"
        :description
        "Learners will receive the Boost only if their selection in every 
         enabled group matches one of the filters you select."}]

      (rf/custom-field
       {:field-key :boost/valid-tags
        :render tag-collections/render
        :validators (mapv
                     (fn [tag-group]
                       {:validator
                        #(let [{:keys [enabled selected]}
                               (get % (:content-provider.tag-group/uuid tag-group))]
                           (not (and enabled (empty? selected))))
                        :message (str (:content-provider.tag-group/name tag-group)
                                      " can't be empty")})
                     activity-valid-tags)
        :default-value (tag-collections/tag-groups->tag-collection-value
                        {:available-tag-groups activity-valid-tags
                         :selected-tag-groups (filter-tag-groups activity-valid-tags valid-tags)
                         :allow-collection-toggling true
                         :description-fn (fn [] "")
                         :label-fn str/capitalize})})])

   [:div {:class (:email-settings-section classes)}
    [:div {:style {:width "35%"}}
     [render-section-details
      {:label "From"
       :description
       (str "Displays in the \"From\" field in the "
            "learner's email client.")}]

     (rf/input
      (cond-> {:field-key :boost/display-name
               :id :display-name
               :type :text
               :placeholder "ArcheMedX"
               :transformers [t/trim]}
        (some? display-name)
        (assoc :default-value display-name)))]

    [:div {:style {:width "calc(65% - 30px)"}}
     [render-section-details
      {:label "Subject Line"
       :description
       (str "Displays in the \"Subject\" field in the "
            "learner's email client.")}]

     (rf/input
      (cond-> {:field-key :boost/subject
               :id :subject
               :type :text
               :transformers [t/trim]}
        (some? subject)
        (assoc :default-value subject)))]]])

(defn- render-learner-performance-section
  [{:keys [show-stats]}]
  [:div {:class [(:section-container classes)
                 (:example-section classes)]}
   [:div
    [render-section-details
     {:label "Include Learner Performance?"
      :description
      (str "When turned on, your boost will include the learner's "
           "activity performance metrics (percent of video viewed; "
           "percent of learning moments engaged; number of notes "
           "and pins).")}]

    (rf/custom-field
     {:render toggle/render
      :field-key :boost/show-stats
      :default-value (or show-stats false)})]
   [:div
    [:img {:src "/images/learner_performance_screenshot.png"
           :class (:example-image classes)}]]])

(defn- render-boost-title-section
  [{:keys [title]}]
  [:div {:class [(:section-container classes)
                 (:example-section classes)]}
   [:div
    [render-section-details
     {:label "Boost Title"
      :description
      (str "Displays at the top of your boost. It "
           "should be no more than 200 characters.")}]

    (rf/input
     (cond-> {:field-key :boost/title
              :id :name
              :type :text
              :validators []
              :transformers [t/trim]}
       (some? title)
       (assoc :default-value title)))]
   [:div
    [:img {:src "/images/boost_title_screenshot.png"
           :class (:example-image classes)}]]])

(defn- render-boost-body-section
  [{:keys [content]} {:keys [activity-uuid]}]
  [:div {:class (:section-container classes)}
   [render-section-details
    {:label "Boost Content"
     :description
     (str "Displays within the main body of your boost. "
          "Use headings and images sparingly to ensure "
          "that they have maximum impact.")}]

   (rf/custom-field
    {:render markup-editor/render
     :field-key :boost/content
     :required false
     :default-value (or content "")
     :supports-images true
     :activity-uuid activity-uuid})])

(def ^{:private true
       :doc "Used to prevent the callback in save-boost! if save fails"}
  save-boost-timeout (atom nil))

(defn- clear-save-boost-timeout!
  []
  (when (some? @save-boost-timeout)
    (js-invoke js/window "clearTimeout" @save-boost-timeout)
    (reset! save-boost-timeout nil)))

(defn- save-boost!
  "Saves the boost by clicking the submit button for the user and then tiggers
  a callback"
  [callback]
  (let [submit-button (-> js/document
                          (js-invoke "getElementById" "boost-form")
                          (js-invoke "querySelectorAll" "input[type=submit]")
                          first)]
    (when (some? callback)
      (reset! save-boost-timeout
              (js-invoke js/window "setTimeout" callback 200)))

    ;; The submit button is not in the DOM after an immediate boost
    ;; has been published (you cannot unpublish an immediate boost)
    (when (some? submit-button)
      (js-invoke submit-button "click"))))

(defn- render-create-questionnaire-btn
  [_]
  (fn [{:keys [is-creating-questionnaire
              activity-uuid
              curriculum-uuid
              boost-uuid]}]
    (let [create-boost-questionnaire
          (fn []
            (save-boost! #(re-frame/dispatch
                           [::>floating/create-floating
                            {:activity/uuid activity-uuid
                             :boost/uuid boost-uuid}
                            {:response-keys [:activity/uuid
                                             :questionnaire/uuid]
                             :on-success (fn [res]
                                           (re-frame/dispatch
                                            [:boosts/legacy-create-boost-questionnaire-success {:boost-uuid boost-uuid
                                                                                                :questionnaire-uuid (:questionnaire/uuid res)}])
                                           (re-frame/dispatch
                                            [:router/nav-to
                                             (str (au/->admin-url {:activity-uuid activity-uuid
                                                                   :curriculum-uuid curriculum-uuid})
                                                  "/boosts/" boost-uuid
                                                  "/edit-questions/questions")]))}])))]
      [:div {:class (:add-button-container classes)}
       [:div {:class (:add-questionnaire-cta classes)}
        [button/icon-button-xl
         {:disabled @is-creating-questionnaire
          :on-click #(when (false? @is-creating-questionnaire)
                       (create-boost-questionnaire))
          :type :button}
         :plus-circle]
        [button/button-link
         {:disabled @is-creating-questionnaire
          :on-click #(when (false? @is-creating-questionnaire)
                       (create-boost-questionnaire))
          :type :button}
         "Add Question(s)"]]])))

(defn- render-questionnaire-section
  [{:keys [questionnaire-uuid] :as boost}
   is-creating-questionnaire
   {:keys [activity-uuid curriculum-uuid]}]
  [:div {:class [(:section-container classes)
                 (:example-section classes)]}
   [:div
    [render-section-details
     {:label "Include a Question(s)?"
      :description
      (str "Adding a question(s) to your boost will drive "
           "learners to reflect on prior learning and allow you "
           "to collect additional learning data.")}]

    (if (some? questionnaire-uuid)
      (let [on-click (fn [_]
                       (save-boost! #(re-frame/dispatch
                                      [:router/nav-to
                                       (str (au/->admin-url
                                             {:activity-uuid activity-uuid
                                              :curriculum-uuid curriculum-uuid})
                                            "/boosts/" (:uuid boost)
                                            "/edit-questions/questions")])))]
        [:div {:class (:add-button-container classes)}
         [:div {:class (:add-questionnaire-cta classes)}
          [button/icon-button-xl
           {:on-click on-click :type :button}
           :plus-circle]
          [button/button-link
           {:on-click on-click :type :button}
           "Edit Question(s)"]]])

      [render-create-questionnaire-btn
       {:is-creating-questionnaire is-creating-questionnaire
        :activity-uuid activity-uuid
        :curriculum-uuid curriculum-uuid
        :boost-uuid (:uuid boost)}])]])

(defn- render-learning-objective-section
  [{:keys [learning-objective-uuid]}
   learning-objectives
   {:keys [activity-uuid curriculum-uuid]}]
  [:div {:class [(:section-container classes)
                 (:example-section classes)]}
   [:div
    [render-section-details
     {:label "Add a Learning Objective?"
      :description
      (str "Linking each boost to a learning objective ensures that "
           "there is alignment with the knowledge, competence, and "
           "skills you are intending to support and extend.")}]

    (rf/custom-field
     {:render select/render
      :label "Learning Objective"
      :field-key :boost/learning-objective-uuid
      :validators []
      :transformers [(fn [value]
                       (if (= value :none) nil value))]
      :default-value (or learning-objective-uuid :none)
      :options (into [{:display "Select a learning objective"
                       :value :none}]
                     (->> @learning-objectives
                          (sort-by :label)
                          (sort-by :ordinal)
                          (mapv (fn [{:keys [uuid label]}]
                                  {:display label :value uuid}))))})]

   [:div {:class (:questionnaire-help-container classes)}
    [text/body-20 :p
     [:span
      "You need to "
      [link/link {:href (str (au/->admin-url
                              {:activity-uuid activity-uuid
                               :curriculum-uuid curriculum-uuid})
                             "/overview")
                  :target "_blank"}
       "create at least one learning objective"]
      " before it can be added to a boost."]]]])

(defn- render-related-resources-section
  [{:keys [show-related-resources resource-uuids]} activity-resources]
  [:div {:class [(:section-container classes)
                 (:example-section classes)]}
   [:div
    [render-section-details
     {:label "Include Related Resources?"
      :description
      (str "When turned on, your boost will include a list of "
           "selected featured resources.")}]

    (rf/custom-field
     {:render toggle/render
      :field-key :boost/show-related-resources
      :default-value (or show-related-resources false)})

    (rf/custom-field
     {:render related-resources-field/render
      :field-key :boost/resource-uuids
      :default-value (set resource-uuids)
      :all-resources activity-resources})]
   [:div
    [:img {:src "/images/boost_resources_screenshot.png"
           :class (:example-image classes)}]]])

(defn- render-include-button-section
  [{:keys [cta-url cta-label]} cta-enabled]
  [:div {:class [(:section-container classes)
                 (:example-section classes)]}
   [:div
    [render-section-details
     {:label "Include a Button?"
      :description
      (str "When turned on, your boost will include a primary call "
           "to action (button) at the bottom of the email. You must "
           "provide both a label and a URL.")}]

    [:div {:class (:cta-options-container classes)}
     (rf/input
      (cond-> {:field-key :boost/cta-label
               :id :cta-label
               :type :text
               :label "Label"
               :validators []
               :transformers [t/trim]}
        (some? cta-label)
        (assoc :default-value cta-label)))

     (rf/input
      (cond-> {:field-key :boost/cta-url
               :id :cta-url
               :type :text
               :label "URL"
               :validators []
               :transformers [t/trim]}
        (some? cta-url)
        (assoc :default-value cta-url)))

     [:div {:class (:include-button-container classes)}
      [checkbox/render-v2
       {:on-change #(reset! cta-enabled (not %))
        :default-value (let [button-disabled
                             (not (and (seq cta-label)
                                       (seq cta-url)))]
                         ;; Will only trigger on mount
                         (reset! cta-enabled cta-enabled)
                         button-disabled)}]
      [text/caption-20 :p
       "Do not include a button"]]]]
   [:div
    [:img {:src "/images/boost_cta_screenshot.png"
           :class (:example-image classes)}]]])

(defn- render-publish-section
  [{:keys [published] :as boost}]
  [:div {:class [(:section-container classes)
                 (:example-section classes)]
         :style {:padding (gs [:spacing :p40])
                 :margin-bottom (gs [:spacing :p0])
                 :border-bottom :none
                 :align-items :center}}
   [:div
    [render-section-details
     {:label (if (is-immediate? boost)
               "Publish & Send your Boost"
               "Publish your Boost")
      :description
      (if (is-immediate? boost)
        (str "Learner's will immediately receive this boost if they have "
             (boost-condition boost))
        (str "Learner's will only receive boosts that are marked "
             "as published."))}]

    [:div {:data-test "boost-slider-parent"}
     (rf/custom-field
      {:render toggle/render
       :field-key :boost/published
       :disabled (and (true? published)
                      (is-immediate? boost))
       :default-value (if (some? published)
                        published false)})]]
   [:div {:style {:display :flex
                  :justify-content :flex-end}}
    [is-live-pill/render
     published (if (is-immediate? boost) "Sent" "Live")]]])

(defn- render-previously-engaged
  [{:keys [send-to-previously-engaged-users processing-stats published]
    :as boost}]
  (let [{:keys [previously-engaged-count] :or {previously-engaged-count 0}}
        processing-stats]
    [:div {:class [(:section-container classes)]
           :style (cond-> {:padding (gs [:spacing :p40])
                           :margin-bottom (gs [:spacing :p0])}
                    (is-immediate? boost)
                    (assoc :padding-top (gs [:spacing :p0])))}

     [render-section-details
      {:label "Previously Engaged Learners"
       :description (str "How to engage with learners that have "
                         "already participated in the activity.")}]

     [text/caption-20 :p
      {:style {:margin-top (gs [:spacing :p20])
               :color (gs [:colors :gray-scale-400])}}
      [:span
       "There are "
       [:span {:style {:font-weight (gs [:font-weight :caption-30])}}
        previously-engaged-count]
       (str " learners that are past the scheduled timing, if you would "
            "like to send the boost to them anyway, select the option below "
            "and when the boost is published it will immediately be sent to "
            "the affected users.")]]

     (rf/custom-field
      {:render toggle/render
       :label (str "On publish: Send immediately to learners past the "
                   "scheduled timing.")
       :disabled (true? published)
       :on-text "yes"
       :off-text "no"
       :field-key :boost/send-to-previously-engaged-users
       :default-value (or send-to-previously-engaged-users false)})]))

(defn- render-scheduled-section
  [{:keys [seconds published] :as boost}]
  (when-not (is-immediate? boost)
    [:div {:class [(:section-container classes)
                   (:example-section classes)]
           :style {:background-color (gs [:colors :gray-scale-50])
                   :padding (gs [:spacing :p40])
                   :margin-bottom (gs [:spacing :p0])}}
     [:div
      [render-section-details
       {:label "Schedule Your Boost"
        :description
        (str "When should your boost be sent?")}]

      [:div {:class (:boost-time-container classes)}
       [:div
        (rf/input
         {:field-key :days
          :id :days
          :type :number
          :disabled published
          :style {:max-width "100px"}
          :validators [{:validator #(some? %)
                        :message "Required"}]
          :masks [t/digit-only]
          :transformers [days->seconds]
          :default-value (seconds->days (or seconds (* 60 60 24 2)))})]

       [text/title-10 :p "day(s)"]

       [:div
        (rf/input
         {:field-key :hours
          :id :hours
          :type :number
          :disabled published
          :style {:max-width "100px"}
          :validators [{:validator #(some? %)
                        :message "Required"}]
          :masks [t/digit-only]
          :transformers [hours->seconds]
          :default-value (seconds->hours (or seconds 0))})]

       [text/title-10 :p "hour(s)"]

       [:div
        (rf/input
         {:field-key :minutes
          :id :minutes
          :type :number
          :disabled published
          :style {:max-width "100px"}
          :validators [{:validator #(some? %)
                        :message "Required"}]
          :masks [t/digit-only]
          :transformers [minutes->seconds]
          :default-value (seconds->minutes (or seconds 0))})]

       [text/title-10 :p "minute(s)"]

       [text/caption-20 :p (str "from when the learner has "
                                (boost-condition boost))]]]

     [:div {:style
            {:padding (gs [:spacing :p40])
             :background-color (gs [:colors :true-white])
             :border (str "1px soild " (gs [:colors :gray-scale-200]))}}
      [text/caption-20 :p
       (str "If \"1 day\" is selected, the boost will be sent the "
            "following calendar day, exactly 24 hours from when the "
            "learner completed the activity")]]]))

(defn- render-boost-stat
  [{:keys [value label]}]
  [:div {:class (:boost-stat-container classes)}
   [text/display-20 :p {:style {:width "150px"
                                :text-align "right"}} value]
   [text/title-10 :p {:style {:margin-bottom (gs [:spacing :p20])}}
    (str (if (= 1 value) "learner" "learners") " " label)]])

(defn- render-boost-stats-section
  [{:keys [processing-stats] :as boost}]
  (let [{:keys [sent-count
                queued-count
                unsubscribed-count
                previously-engaged-count]
         :or {previously-engaged-count 0}}
        processing-stats]
    [:div {:class [(:section-container classes)]
           :style {:padding (gs [:spacing :p40])
                   :margin-bottom (gs [:spacing :p0])}}
     [render-section-details
      {:label "Boost Stats"
       :description "How many learners have been sent this boost?"}]

     [render-boost-stat
      {:value sent-count
       :label "have received this boost."}]

     (when-not (is-immediate? boost)
       [render-boost-stat
        {:value queued-count
         :label "are scheduled to receive this boost."}])

     (when-not (is-immediate? boost)
       [render-boost-stat
        {:value previously-engaged-count
         :label (str "have gone through the activity before the boost "
                     "was published and are not scheduled to receive it.")}])

     [render-boost-stat
      {:value unsubscribed-count
       :label (str "meet the criteria to receive this boost but have "
                   "unsubscribed from boost emails.")}]]))

(defn- should-notifiy-changes?
  "Check to see if a message should be displayed to the admin."
  [{:keys [new old]}]
  (and (-> old :published boolean false?)
       (-> new :boost/published true?)))

(defn identical-tag-ids? [tag-map content-provider-map]
  (let [tag-ids (set (mapcat (fn [[_ tags]] (map :tag-id tags)) tag-map))
        content-uuids (set (mapcat (fn [[_ tags]] (map :content-provider.tag/uuid tags)) content-provider-map))]
    (= tag-ids content-uuids)))

(defn- tag-groups-changed?
  "Check if the tag-groups have changed"
  [{:keys [new old]}]
  (false? (identical-tag-ids? (:valid-tags old) (:boost/valid-tags new))))

(defn render
  [{:keys [params]}]
  (let [{:keys [activity-uuid boost-uuid]} params
        is-creating-questionnaire (re-frame/subscribe
                                   [:request/is-dispatching
                                    :boosts/create-boost-questionnaire])
        boosts (re-frame/subscribe [:boosts])

        activity
        (re-frame/subscribe [::kyber-db/get-record
                             {:model :activity
                              :ident activity-uuid}])

        activity-valid-tags (:activity/valid-tags @activity)

        cta-enabled (reagent/atom false)
        learning-objectives (re-frame/subscribe [:learning-objectives])
        initial-boost (->> @boosts
                           (filter #(= (:uuid %) boost-uuid))
                           first)

        current-subject (atom (:subject initial-boost))
        current-published (atom (:published initial-boost))]
    
    (fn [{:keys [params]}]
      (let [{:keys [activity-uuid curriculum-uuid boost-uuid]}
            params

            event-key
            ::>boosts/update-boost

            delete-event-key
            :boosts/delete-boost

            is-submitting
            (re-frame/subscribe [:request/is-dispatching event-key])

            boost
            (->> @boosts
                 (filter #(= (:uuid %) boost-uuid))
                 (first))
                        
            {activity-resources :activity/resources}
            @activity

            {boost-uuid :uuid
             boost-name :name
             system-type :system-type
             published :published
             publish-at :publish-at
             valid-tags :valid-tags}
            boost]
        [rf/form {:id event-key
                  :is-submitting is-submitting}
         [:form
          {:id "boost-form"
           :rf/form
           {:on-submit
            (fn [{update-published :boost/published
                 update-subject :boost/subject
                 :as updated-boost}]
                
              (if (and (true? update-published)
                       (empty? update-subject))
                (do
                  (clear-save-boost-timeout!)

                  (re-frame/dispatch
                   [:notifier/create-alert
                    {:title "Subject Missing"
                     :message (str "Please set a subject before "
                                   "you publish this boost.")
                     :confirm-only true}]))
                (let [current-boost-data (->> @boosts
                                               (filter #(= (:uuid %) boost-uuid))
                                               first)

                      update-action
                      [event-key
                       (cond-> (-> updated-boost
                                   (assoc :boost/uuid boost-uuid
                                          :boost/publish-at publish-at
                                          :boost/show-banner true
                                          :boost/activity-uuid activity-uuid
                                          :boost/seconds (+ (:days updated-boost)
                                                            (:hours updated-boost)
                                                            (:minutes updated-boost))
                                          :curriculum-uuid curriculum-uuid
                                          :prevent-redirect true)
                                   (dissoc :days :hours :minutes))
                         (false? @cta-enabled)
                         (assoc :boost/cta-url ""
                                :boost/cta-label ""))]

                      save-fn
                      #(do
                         (reset! current-subject (:boost/subject updated-boost))
                         (reset! current-published (:boost/published updated-boost))
                         (re-frame/dispatch update-action))]

                  (cond
                    (should-notifiy-changes? {:old {:published @current-published}
                                              :new updated-boost})
                    (do
                      (clear-save-boost-timeout!)
                      (re-frame/dispatch
                       [:notifier/create-alert
                        {:title "Publish Boost?"
                         :message (if (is-immediate? boost)
                                    (str "You are about to publish this boost. It will "
                                         "be immediately emailed to learners who have "
                                         (boost-condition boost :past))
                                    (cond->
                                     (str "You are about to publish this boost. It "
                                          "will be emailed to learners who have "
                                          (boost-condition boost :past))
                                      
                                      (true? (:send-to-previously-engaged-users updated-boost))
                                      (str " When you publish the boost it will be "
                                           "immediately sent to learners that have passed the scheduled "
                                           "timing.")

                                      (false? (:send-to-previously-engaged-users updated-boost))
                                      (str " When you publish the boost you have "
                                           "selected to exclude learners that have already passed the "
                                           "scheduled timing.")))

                         :confirm-text "Publish"
                         :confirm-action save-fn}]))

                    (and (true? (:published current-boost-data))
                         (tag-groups-changed? {:old current-boost-data
                                               :new updated-boost}))
                    (do
                      (clear-save-boost-timeout!)
                      (re-frame/dispatch
                       [:notifier/create-alert
                        {:title "Enabling Sites and Roles?"
                         :message "This Boost is published. Are you sure you want to change the Sites and Roles receiving this Boost?"
                         :confirm-text "Yes"
                         :confirm-action save-fn}]))

                    :else
                    (save-fn)))))}}
          
          [:div {:class (:form-contianer classes)}
           [render-inline-name-editing {:boost-name boost-name
                                        :system-type system-type
                                        :event-key event-key
                                        :curriculum-uuid curriculum-uuid
                                        :activity-uuid activity-uuid
                                        :boost-uuid boost-uuid
                                        :published published
                                        :publish-at publish-at}]

           (render-email-settings-section boost activity-valid-tags valid-tags)

           [:div {:class (:content-section classes)}
            [text/title-30 :h4
             {:class (:content-section-title classes)}
             "Content"]

            [:div {:class (:content-inner-section classes)}
             (render-boost-title-section boost)

             (render-boost-body-section boost {:activity-uuid activity-uuid})

             (render-learner-performance-section boost)

             (render-questionnaire-section
              boost
              is-creating-questionnaire
              {:activity-uuid activity-uuid
               :curriculum-uuid curriculum-uuid})

             (render-learning-objective-section
              boost learning-objectives
              {:activity-uuid activity-uuid
               :curriculum-uuid curriculum-uuid})

             (render-related-resources-section boost activity-resources)

             (render-include-button-section boost cta-enabled)]]

           (render-scheduled-section boost)

           (when-not (is-immediate? boost)
             (render-previously-engaged boost))

           (render-boost-stats-section boost)

           (render-publish-section boost)

           [:div {:class (:cta-container classes)}
            [:div {:style {:display :flex
                           :align-items :flex-end}}
             (if (and (true? published)
                      (is-immediate? boost))
               [button/primary-button
                {:disabled true
                 :on-click identity
                 :type :button
                 :style {:margin-top (gs [:spacing :p20])}}
                "Boost Sent"]
               (rf/submit-button {:default-text "Update Boost"}))

             [button/secondary-button
              {:type :button
               :style {:margin-left (gs [:spacing :p20])}
               :on-click
               (fn []
                 (save-boost!
                  (fn []
                    (if (empty? @current-subject)
                      (re-frame/dispatch
                       [:notifier/create-alert
                        {:title "Subject Not Set"
                         :message (str "Please set a subject before sending "
                                       "a test email.")
                         :confirm-only true}])
                      (re-frame/dispatch
                       [:boosts/send-test-boost
                        {:activity-uuid activity-uuid
                         :boost-uuid boost-uuid}])))))}
              "Send test boost"]]

            [:div {:style {:display :flex}}
             [link/secondary-anchor-button
              {:type :button
               :href (str (au/->admin-url {:activity-uuid activity-uuid
                                           :curriculum-uuid curriculum-uuid})
                          "/boosts")}
              "Close"]
             [button/admin-button
              {:type :button
               :style {:margin-left (gs [:spacing :p20])}
               :disabled (true? published)
               :on-click
               (fn []
                 (re-frame/dispatch
                  [:notifier/create-alert
                   {:title "Are you sure you want to remove this boost?"
                    :message (str "By removing a boost, learner's will no "
                                  "longer receive any more emails even if "
                                  "they are currently scheduled.")
                    :confirm-text "Remove Boost"
                    :confirm-action #(re-frame/dispatch
                                      [delete-event-key
                                       {:activity-uuid activity-uuid
                                        :curriculum-uuid curriculum-uuid
                                        :boost-uuid boost-uuid}])}]))}
              "Remove Boost"]]]]]]))))
